// @flow
import React, { PureComponent } from 'react';
import styled from 'styled-components';
import Form, { Field } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';
import Modal, { ModalBody, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import InfoIcon from '@atlaskit/icon/glyph/info';
import { DatePicker } from '@atlaskit/datetime-picker';
import TextArea from '@atlaskit/textarea';
import Button from '@atlaskit/button';
import { graphql, compose, withApollo } from 'react-apollo';
import { isBefore } from 'date-fns';
import { CREATE_VIRTUAL_COMPONENT_RELEASE } from '../../graphql/mutations';
import ProjectVersionSteps from './step-component/ProjectVersionSteps';
import { analytics } from '../../services/analytics';
import VirtualComponentSelect, { parseVirtualComponentToOption } from '../select/VirtualComponentSelect';

const Footer = styled.div`
    display: flex;
    flex-direction: row-reverse;
    padding: 15px 0;
    button {
        margin: 2px;
    }
`;

const FieldsWrapper = styled.div`
    padding-bottom: 15px;
`;

class CreateComponentVersionModal extends PureComponent {
    state = {
        newComponentVersionName: '',
        newComponentVersionComponent: '',
        newComponentVersionStartAt: '',
        newComponentVersionEndAt: '',
        newComponentVersionDescription: '',
        newComponentVersionLinkedVersions: '',
        isDisabled: false,
    };

    onFormClosed = () => {
        this.setState(
            {
                newComponentVersionName: '',
                newComponentVersionComponent: '',
                newComponentVersionStartAt: '',
                newComponentVersionEndAt: '',
                newComponentVersionDescription: '',
                newComponentVersionLinkedVersions: '',
            },
            this.props.onFormClosed,
        );
    };

    getLinkedProjectVersionsData = (versions) => {
        return versions
            ? versions.map((v) => {
                  return {
                      jiraProjectId: parseInt(v.triggerValue, 10),
                      jiraProjectVersionId: parseInt(v.value, 10),
                  };
              })
            : [];
    };

    onFormSubmit = () => {
        this.setState(
            {
                isDisabled: true,
            },
            () => {
                const input = {
                    name: this.state.newComponentVersionName,
                    component_id: this.state.newComponentVersionComponent.value,
                    description: this.state.newComponentVersionDescription,
                    linked_versions: this.getLinkedProjectVersionsData(this.state.newComponentVersionLinkedVersions),
                    start_at: this.state.newComponentVersionStartAt,
                    end_at: this.state.newComponentVersionEndAt,
                };

                if (!this.state.newComponentVersionName || !this.state.newComponentVersionComponent.value) {
                    this.setState(
                        {
                            isDisabled: false,
                        },
                        () => {
                            this.props.addFlag({
                                created: Date.now(),
                                icon: <InfoIcon primaryColor="#253858" />,
                                title: 'An error occurred.',
                                description: 'Fill all required fields',
                                key: 'warning',
                            });
                        },
                    );
                } else {
                    // CREATE_VIRTUAL_COMPONENT_RELEASE
                    this.props
                        .createVirtualComponentRelease({
                            variables: { input },
                        })
                        .then((response) => {
                            if (response.data.createVirtualComponentRelease.success) {
                                this.props.onRefetchRequest();
                                this.onFormClosed();
                            } else {
                                this.setState(
                                    {
                                        isDisabled: false,
                                    },
                                    () => {
                                        this.props.addFlag({
                                            created: Date.now(),
                                            icon: <InfoIcon primaryColor="#253858" />,
                                            title: 'An error occurred.',
                                            description:
                                                'A component version with this name already exists. Change the name and try again.',
                                            key: 'warning',
                                        });
                                    },
                                );
                            }

                            analytics.track({
                                userId: window.license,
                                event: 'created_component_version',
                                timestamp: new Date(),
                                properties: {
                                    addon_key: process.env.REACT_APP_KEY,
                                    linked_to_project_version:
                                        input.linked_versions && input.linked_versions.length > 0,
                                },
                            });
                        })
                        .catch(() => {
                            this.setState(
                                {
                                    isDisabled: false,
                                },
                                () => {
                                    this.props.addFlag({
                                        created: Date.now(),
                                        icon: <ErrorIcon primaryColor="#DE350B" />,
                                        title: 'An error occurred.',
                                        description: 'Component version not created.',
                                        key: 'error',
                                    });
                                    this.onFormClosed();
                                },
                            );
                        });
                }
            },
        );
    };

    render = () => (
        <ModalTransition>
            <Modal {...this.props} shouldScrollInViewport onClose={this.onFormClosed}>
                <ModalHeader>
                    <ModalTitle>Create component version</ModalTitle>
                </ModalHeader>
                <ModalBody>
                    <Form onSubmit={this.onFormSubmit}>
                        {({ formProps }) => (
                            <form {...formProps}>
                                <FieldsWrapper>
                                    <Field label="Version name" name="version-name" isRequired>
                                        {({ fieldProps }) => (
                                            <Textfield
                                                {...fieldProps}
                                                value={this.state.newComponentVersionName}
                                                onChange={(e) => {
                                                    this.setState({
                                                        newComponentVersionName: e.target.value,
                                                    });
                                                }}
                                            />
                                        )}
                                    </Field>

                                    <Field label="Component" name="virtual_component_id" isRequired>
                                        {({ fieldProps }) => (
                                            <VirtualComponentSelect
                                                {...fieldProps}
                                                value={this.state.newComponentVersionComponent}
                                                onChange={(selected) => {
                                                    this.setState({
                                                        newComponentVersionComponent: selected,
                                                    });
                                                }}
                                                placeholder="Select component"
                                                defaultOptions={
                                                    this.props.componentParentsDefaultOptions?.length
                                                        ? this.props.componentParentsDefaultOptions.map(
                                                              parseVirtualComponentToOption,
                                                          )
                                                        : true
                                                }
                                            />
                                        )}
                                    </Field>

                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <div style={{ width: '100%', marginRight: '5px' }}>
                                            <Field label="Start date" name="start_date">
                                                {({ fieldProps }) => (
                                                    <DatePicker
                                                        {...fieldProps}
                                                        id="start_date"
                                                        value={this.state.newComponentVersionStartAt || ''}
                                                        placeholder="eg. 07/22/2020"
                                                        onChange={(date) => {
                                                            this.setState({
                                                                newComponentVersionStartAt: date,
                                                            });
                                                            if (
                                                                this.state.newComponentVersionEndAt &&
                                                                isBefore(
                                                                    new Date(this.state.newComponentVersionEndAt),
                                                                    new Date(date),
                                                                )
                                                            ) {
                                                                this.setState({
                                                                    newComponentVersionEndAt: date,
                                                                });
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </Field>
                                        </div>
                                        <div style={{ width: '100%', marginLeft: '5px' }}>
                                            <Field label="Release date" name="release_date">
                                                {({ fieldProps }) => (
                                                    <DatePicker
                                                        {...fieldProps}
                                                        id="release_date"
                                                        minDate={this.state.newComponentVersionStartAt || ''}
                                                        value={this.state.newComponentVersionEndAt || ''}
                                                        placeholder="eg. 07/22/2020"
                                                        onChange={(date) => {
                                                            this.setState({
                                                                newComponentVersionEndAt: date,
                                                            });
                                                        }}
                                                    />
                                                )}
                                            </Field>
                                        </div>
                                    </div>
                                    <Field label="Description" name="description">
                                        {({ fieldProps }) => (
                                            <TextArea
                                                {...fieldProps}
                                                value={this.state.newComponentVersionDescription || ''}
                                                onChange={(e) => {
                                                    this.setState({
                                                        newComponentVersionDescription: e.target.value,
                                                    });
                                                }}
                                            />
                                        )}
                                    </Field>
                                    <Field label="Link to project versions" name="link_to_project_versions">
                                        {({ fieldProps }) => (
                                            <ProjectVersionSteps
                                                {...fieldProps}
                                                value={this.state.newComponentVersionLinkedVersions}
                                                onChange={(options) => {
                                                    this.setState({
                                                        newComponentVersionLinkedVersions: options,
                                                    });
                                                }}
                                            />
                                        )}
                                    </Field>
                                </FieldsWrapper>
                                <Footer>
                                    <Button
                                        type="submit"
                                        appearance="primary"
                                        iconAfter={null}
                                        isDisabled={this.state.isDisabled}
                                    >
                                        Save
                                    </Button>
                                    <Button onClick={this.onFormClosed}>Cancel</Button>
                                </Footer>
                            </form>
                        )}
                    </Form>
                </ModalBody>
            </Modal>
        </ModalTransition>
    );
}

export default compose(
    withApollo,
    graphql(CREATE_VIRTUAL_COMPONENT_RELEASE, { name: 'createVirtualComponentRelease' }),
)(CreateComponentVersionModal);
